import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import AppLogo from './AppLogo';
import useVisiblePoll from '../hooks/useVisiblePoll';

const SIBLINGS = [
  {
    name: 'DomainProtection',
    tagline: 'Brand & typosquatting protection',
    body: 'Detect lookalike domains targeting your organization, score their risk, and track takedowns.',
    accent: '#c85448',
    url: '/domainprotection/',
    healthUrl: '/api/health',
  },
  {
    name: 'PhishDesk',
    tagline: 'Phishing email triage',
    body: 'Analyze reported emails, pull out IOCs, draft analyst replies, and keep a running report log.',
    accent: '#a78bfa',
    url: '/phishdesk/',
    healthUrl: '/phishdesk-api/health',
  },
];

/* Live per-tool status dot. Any response below 500 counts as online (an auth
   wall on a health route still means the service is up); network errors and
   5xx show as unreachable; unknown until the first probe returns. */
function useToolStatus(healthUrl) {
  const [status, setStatus] = useState('unknown');
  useVisiblePoll(async () => {
    if (!healthUrl) return;
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 5000);
    try {
      const res = await fetch(healthUrl, { signal: ctrl.signal, cache: 'no-store' });
      // An auth wall (401/403) still means the service is up. A 200 must be
      // JSON: a missing tool falls through to the SPA's index.html with 200
      const json = (res.headers.get('content-type') || '').includes('application/json');
      setStatus(res.status === 401 || res.status === 403 || (res.ok && json) ? 'online' : 'down');
    } catch {
      setStatus('down');
    } finally {
      clearTimeout(timer);
    }
  }, 60000, [healthUrl]);
  return status;
}

function StatusBadge({ status }) {
  const meta = status === 'online'
    ? { color: '#4ade80', label: 'online' }
    : status === 'down'
    ? { color: '#f87171', label: 'unreachable' }
    : { color: 'rgba(232,228,248,0.25)', label: 'checking…' };
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6 }}>
      <Box sx={{
        width: 7, height: 7, borderRadius: '50%', bgcolor: meta.color,
        boxShadow: status === 'online' ? `0 0 6px ${meta.color}` : 'none',
      }} />
      <Typography component="span" sx={{ fontSize: '0.68rem', color: 'rgba(232,228,248,0.45)' }}>
        {meta.label}
      </Typography>
    </Box>
  );
}

function ToolCard({ tool }) {
  const status = useToolStatus(tool.healthUrl);
  return (
    <Box
      component="a"
      href={tool.url}
      sx={{
        position: 'relative',
        p: '1.5rem 1.5rem 1.75rem',
        borderRadius: 1.5,
        bgcolor: 'rgba(26,22,37,0.8)',
        border: '1px solid rgba(255,255,255,0.07)',
        textDecoration: 'none',
        color: 'inherit',
        overflow: 'hidden',
        transition: 'border-color 0.15s, transform 0.15s, background 0.15s',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          bgcolor: tool.accent,
          opacity: 0.85,
        },
        '&:hover': {
          borderColor: tool.accent,
          bgcolor: 'rgba(26,22,37,0.95)',
          transform: 'translateY(-2px)',
        },
        '&:focus-visible': {
          outline: `2px solid ${tool.accent}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mt: 0.25, mb: 0.35 }}>
        <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: '#fff' }}>
          {tool.name}
        </Typography>
        <StatusBadge status={status} />
      </Box>
      <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: 'rgba(232,228,248,0.6)', mb: 1.25 }}>
        {tool.tagline}
      </Typography>
      <Typography sx={{ fontSize: '0.78rem', color: 'rgba(232,228,248,0.45)', lineHeight: 1.55 }}>
        {tool.body}
      </Typography>
      <Typography sx={{ mt: 1.25, fontSize: '0.78rem', fontWeight: 600, color: tool.accent, letterSpacing: '0.02em' }}>
        Open {tool.name} →
      </Typography>
    </Box>
  );
}

export default function Landing() {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        px: { xs: 3, sm: 4 },
        py: 6,
        bgcolor: '#0c0a18',
        backgroundImage:
          "radial-gradient(ellipse at top, rgba(167,139,250,0.08), transparent 60%), " +
          "radial-gradient(ellipse at bottom, rgba(200,84,72,0.05), transparent 60%)",
      }}
    >
      <Box sx={{ mt: { xs: 4, sm: 6 }, mb: 2 }}>
        <AppLogo size={140} />
      </Box>
      <Typography
        variant="h2"
        sx={{
          fontSize: { xs: '2.25rem', sm: '2.75rem' },
          fontWeight: 700,
          letterSpacing: '-0.03em',
          color: '#e8e4f8',
          mb: 1,
        }}
      >
        {/* TEMP (branding hidden for demo build): was "CertShield". See BRANDING_NEUTRALIZATION.md */}
        Security Tools
      </Typography>
      <Typography sx={{ color: 'rgba(232,228,248,0.55)', fontSize: '0.95rem', mb: 5 }}>
        Umbrella platform for security tooling.
      </Typography>
      <Box
        component="nav"
        aria-label="applications"
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          gap: 2.5,
          width: '100%',
          maxWidth: 720,
        }}
      >
        {SIBLINGS.map((s) => <ToolCard key={s.name} tool={s} />)}
      </Box>
    </Box>
  );
}
